import { Action, Reducer } from 'redux'
import { AppThunkAction, AppThunkFunc } from '.'
import { UsersClient, UserWithOrganizationResponseDTO, AdminUserRequestDTO, OrganizationsClient, RoleValues } from '../generated/ApiClient'
import { actionCreators as snackbar, SnackbarAction } from './AlertSnackbarStore'

export const PagingAmount = 10

export interface UsersState {
    isLoading: boolean
    byId: { [key: string]: UserWithOrganizationResponseDTO }
}

export interface User {
    name: string | undefined,
    phone: string | undefined,
    email: string | undefined,
}

interface RequestUsersAction {
    type: 'users/RequestUsers'
}

interface RequestUserAction {
    type: 'users/RequestUser'
}

interface ResponseUsersAction {
    type: 'users/ReceiveUsers'
    users: UserWithOrganizationResponseDTO[]
}

interface RemoveUser {
    type: 'users/RemoveUser'
    id: string
}

type UserAction = RequestUsersAction | RequestUserAction | ResponseUsersAction | RemoveUser

export const actionCreators = {
    requestUsers: (): AppThunkAction<UserAction> => async (dispatch, getState) => {
        dispatch({ type: 'users/RequestUsers' })
        const users = await new UsersClient().getMany3()
        dispatch({ type: 'users/ReceiveUsers', users: users })
    },
    requestUser: (id: string): AppThunkAction<UserAction> => async (dispatch, getState) => {
        dispatch({ type: 'users/RequestUser' })
        const user = await new UsersClient().get6(id)
        dispatch({ type: 'users/ReceiveUsers', users: [user] })
    },
    updateUser: (user: UserWithOrganizationResponseDTO, newOrganization: string | undefined, newRole: RoleValues | undefined): AppThunkAction<UserAction | SnackbarAction> => async (dispatch, getState) => {
        try {
            const shouldUpdateOrg = newOrganization !== user.organization?.externalId
            const shouldUpdateRole = newRole && (user.role.Case !== newRole || shouldUpdateOrg)
            if (shouldUpdateOrg || shouldUpdateRole) {
                if (shouldUpdateOrg) {
                    if (user.organization?.externalId)
                        await new OrganizationsClient().deleteUser(user.organization.externalId, user.azureId)
                    if (newOrganization !== undefined)
                        await new OrganizationsClient().addUser(newOrganization, user.azureId)
                }
                if (shouldUpdateRole) {
                    const users = await new UsersClient().update5(user.azureId, { role: { Case: newRole! } })
                }
                const updatedUser = await new UsersClient().get6(user.azureId)
                dispatch({ type: 'users/ReceiveUsers', users: [updatedUser] })
                dispatch(snackbar.addMessage({ title: "Info", message: 'Member saved successfully.', alertType: 'success' }))
            }
            else
                dispatch(snackbar.addMessage({ title: "Info", message: 'Member saved successfully.', alertType: 'info' }))
        }
        catch (error) {
            dispatch(snackbar.addError(error, undefined, 'Failed to save member'))
        }
    },
    removeUser: (id: string): AppThunkAction<UserAction | SnackbarAction> => async (dispatch, getState) => {
        try {
            const result = await new UsersClient().delete4(id)
            dispatch({ type: 'users/RemoveUser', id })
            dispatch(snackbar.addMessage({ title: "Removed member", message: 'Successfully removed member', alertType: 'success' }))
        }
        catch (error) {
            dispatch(snackbar.addError(error, undefined, 'Failed to remove member'))
        }
    },
    addUserToOrganization: (organizationId: string, userIdOrEmail: string): AppThunkAction<UserAction | SnackbarAction> => async (dispatch, getState) => {
        try {
            const result = await new OrganizationsClient().addUser(organizationId, userIdOrEmail)
            const updatedUser = await new UsersClient().get6(result.azureId)
            dispatch({ type: 'users/ReceiveUsers', users: [updatedUser] })
            dispatch(snackbar.addMessage({ title: "Removed member from organization", message: 'Successfully removed member from organization', alertType: 'success' }))
        }
        catch (error) {
            dispatch(snackbar.addError(error, undefined, 'Failed to remove member'))
        }
    }
}

const unloadedState: UsersState = {
    isLoading: false,
    byId: {},
}

export const reducer: Reducer<UsersState> = (state: UsersState | undefined, incomingAction: Action): UsersState => {
    if (state === undefined)
        return unloadedState

    const action = incomingAction as UserAction
    switch (action.type) {
        case 'users/RequestUsers':
        case 'users/RequestUser':
            return {
                ...state,
                isLoading: true
            }
        case 'users/ReceiveUsers':
            return {
                ...state,
                isLoading: false,
                byId: {
                    ...state.byId,
                    ...Object.fromEntries(action.users.map(s => [s.azureId, s])) //TODO this is wrong
                }
            }
        case 'users/RemoveUser':
            const id = action.id
            const { [id]: value, ...withoutRemoved } = state.byId;
            return {
                ...state,
                byId: withoutRemoved
            }
        default:
            return state
    }
}